"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.verify = exports.sign = exports.getStarkKey = exports.getKeyPair = exports.genKeyPair = exports.ec = void 0;
var elliptic_1 = require("elliptic");
var hash_js_1 = __importDefault(require("hash.js"));
var minimalistic_assert_1 = __importDefault(require("minimalistic-assert"));
var constants_1 = require("./constants");
var encode_1 = require("./utils/encode");
var number_1 = require("./utils/number");
exports.ec = new elliptic_1.ec(new elliptic_1.curves.PresetCurve({
    type: 'short',
    prime: null,
    p: constants_1.FIELD_PRIME,
    a: '00000000 00000000 00000000 00000000 00000000 00000000 00000000 00000001',
    b: '06f21413 efbe40de 150e596d 72f7a8c5 609ad26c 15c915c1 f4cdfcb9 9cee9e89',
    n: constants_1.EC_ORDER,
    hash: hash_js_1.default.sha256,
    gRed: false,
    g: constants_1.CONSTANT_POINTS[1],
}));
/*
 The function _truncateToN in lib/elliptic/ec/index.js does a shift-right of 4 bits
 in some cases. This function does the opposite operation so that
   _truncateToN(fixMessage(msg)) == msg.
*/
function fixMessage(msg) {
    var pureHex = msg.replace(/^0x0*/, '');
    if (pureHex.length <= 62) {
        // In this case, pureHex should not be transformed, as the byteLength() is at most 31,
        // so delta < 0 (see _truncateToN).
        return pureHex;
    }
    (0, minimalistic_assert_1.default)(pureHex.length === 63);
    // In this case delta will be 4 so we perform a shift-left of 4 bits by adding a ZERO_BN.
    return pureHex + "0";
}
exports.genKeyPair = exports.ec.genKeyPair.bind(exports.ec);
function getKeyPair(pk) {
    var pkBn = (0, number_1.toBN)(pk);
    return exports.ec.keyFromPrivate((0, encode_1.removeHexPrefix)((0, number_1.toHex)(pkBn)), 'hex');
}
exports.getKeyPair = getKeyPair;
function getStarkKey(keyPair) {
    // this method needs to be run to generate the .pub property used below
    // the result can be dumped
    keyPair.getPublic(true, 'hex');
    return (0, encode_1.addHexPrefix)((0, encode_1.sanitizeBytes)(keyPair.pub.getX().toString(16), 2));
}
exports.getStarkKey = getStarkKey;
/*
 Signs a message using the provided key.
 key should be an KeyPair with a valid private key.
 Returns an Signature.
*/
function sign(keyPair, msgHash) {
    var msgHashBN = (0, number_1.toBN)((0, encode_1.addHexPrefix)(msgHash));
    // Verify message hash has valid length.
    (0, number_1.assertInRange)(msgHashBN, constants_1.ZERO, (0, number_1.toBN)((0, encode_1.addHexPrefix)(constants_1.MAX_ECDSA_VAL)), 'msgHash');
    var msgSignature = keyPair.sign(fixMessage(msgHash));
    var r = msgSignature.r, s = msgSignature.s;
    var w = s.invm(exports.ec.n);
    // Verify signature has valid length.
    (0, number_1.assertInRange)(r, constants_1.ONE, (0, number_1.toBN)((0, encode_1.addHexPrefix)(constants_1.MAX_ECDSA_VAL)), 'r');
    (0, number_1.assertInRange)(s, constants_1.ONE, (0, number_1.toBN)((0, encode_1.addHexPrefix)(constants_1.EC_ORDER)), 's');
    (0, number_1.assertInRange)(w, constants_1.ONE, (0, number_1.toBN)((0, encode_1.addHexPrefix)(constants_1.MAX_ECDSA_VAL)), 'w');
    return msgSignature;
}
exports.sign = sign;
/*
   Verifies a message using the provided key.
   key should be an KeyPair with a valid public key.
   msgSignature should be an Signature.
   Returns a boolean true if the verification succeeds.
  */
function verify(keyPair, msgHash, sig) {
    var msgHashBN = (0, number_1.toBN)((0, encode_1.addHexPrefix)(msgHash));
    (0, number_1.assertInRange)(msgHashBN, constants_1.ZERO, (0, number_1.toBN)((0, encode_1.addHexPrefix)(constants_1.MAX_ECDSA_VAL)), 'msgHash');
    var r = sig.r, s = sig.s;
    var w = s.invm(exports.ec.n);
    // Verify signature has valid length.
    (0, number_1.assertInRange)(r, constants_1.ONE, (0, number_1.toBN)((0, encode_1.addHexPrefix)(constants_1.MAX_ECDSA_VAL)), 'r');
    (0, number_1.assertInRange)(s, constants_1.ONE, (0, number_1.toBN)((0, encode_1.addHexPrefix)(constants_1.EC_ORDER)), 's');
    (0, number_1.assertInRange)(w, constants_1.ONE, (0, number_1.toBN)((0, encode_1.addHexPrefix)(constants_1.MAX_ECDSA_VAL)), 'w');
    return keyPair.verify(fixMessage(msgHash), sig);
}
exports.verify = verify;
